/**
 * The state of one site's development brain, and the button that starts it.
 *
 * Deliberately NOT a copy of `useEntityScan`. That hook is idle-until-clicked
 * and forgets everything on unmount, which is right for a request that answers
 * in a second. A brain run is tens of seconds of work that lives in a database
 * row, so this one behaves like something that outlives the page:
 *
 *  - on mount it READS the stored row. A finished run from last week is shown
 *    immediately, and nothing is fetched from the prospect's server.
 *  - a row that says `running` is polled until it stops, which is what makes
 *    the run survive an operator navigating away and coming back.
 *  - the start button appears only when there is nothing to show or the run is
 *    over. "Idle by design reads as dead" is a documented failure here; so is
 *    a second button press that quietly crawls somebody's website twice.
 *
 * A 404 is not an error: it is the ordinary answer for a site nobody has run
 * yet, and it is what puts the card into its inviting empty state.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../../../lib/api";
import { getSiteBrain, rethinkSiteBrain, runSiteBrain, type SiteBrain } from "../../../../lib/siteBrain";

/** How often a running row is re-read. R1 runs take tens of seconds. */
const POLL_MS = 3000;

/** Turn a failed request into a key the catalogue translates. */
export function brainErrorKey(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string }) : null;
  if (body?.error === "invalid_url") return "brain.error.invalidUrl";
  if (body?.error === "already_running") return "brain.error.alreadyRunning";
  if (body?.error === "site_excluded") return "brain.error.excluded";
  if (body?.error === "forbidden") return "brain.error.forbidden";
  // Re-think refusals. Kept apart because they need different actions: one is
  // fixed by pressing the other button, the other means this site was never read.
  if (body?.error === "no_run") return "brain.error.noRun";
  if (body?.error === "not_found") return "brain.error.notFound";
  return "brain.error.failed";
}

export interface UseBrain {
  brain: SiteBrain | null;
  /** First read in flight. Distinct from `running`, which is the job. */
  loading: boolean;
  /** A run is in flight on the server. */
  running: boolean;
  /** The start request itself is in flight. */
  starting: boolean;
  /** Translation key, or null. */
  errorKey: string | null;
  start: () => Promise<void>;
  /** Re-run the model passes over the pages already stored. No crawl, no browser. */
  rethink: () => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * @param domain the registrable domain, the same key the scanner uses
 * @param onFinished called once when a run this hook was watching completes
 */
export function useBrain(domain: string, onFinished?: (brain: SiteBrain) => void): UseBrain {
  const [brain, setBrain] = useState<SiteBrain | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Only a transition this component actually watched is worth announcing.
  // Without the flag, mounting on an already-finished row would announce a run
  // that ended yesterday - the same spurious-fire trap the shell's signal
  // consumers have to avoid.
  const sawRunning = useRef(false);
  const alive = useRef(true);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const read = useCallback(async (): Promise<SiteBrain | null> => {
    try {
      const next = await getSiteBrain(domain);
      if (!alive.current) return null;

      if (next.status === "running") {
        sawRunning.current = true;
      } else if (sawRunning.current) {
        sawRunning.current = false;
        finishedRef.current?.(next);
      }
      setBrain(next);
      setErrorKey(null);
      return next;
    } catch (err) {
      if (!alive.current) return null;
      // Never run is the ordinary state of most sites, not a failure.
      if (err instanceof ApiError && err.status === 404) {
        setBrain(null);
        setErrorKey(null);
        return null;
      }
      setErrorKey(brainErrorKey(err));
      return null;
    }
  }, [domain]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void read().finally(() => {
      if (alive.current) setLoading(false);
    });
    return () => {
      alive.current = false;
    };
  }, [read]);

  // The poll exists only while there is something to poll for. A timer that
  // keeps running against a finished row is a request per operator per three
  // seconds, forever, for an answer that cannot change.
  const running = brain?.status === "running";
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void read(), POLL_MS);
    return () => clearInterval(timer);
  }, [running, read]);

  /**
   * Both buttons behave identically from here: claim, then read straight back
   * so the card flips to "running" without waiting a whole poll interval. A
   * button that looks like it did nothing gets pressed again, and the second
   * press is another crawl of somebody's website.
   */
  const begin = useCallback(
    async (request: (domain: string) => Promise<unknown>) => {
      setStarting(true);
      setErrorKey(null);
      try {
        await request(domain);
        sawRunning.current = true;
        await read();
      } catch (err) {
        if (alive.current) setErrorKey(brainErrorKey(err));
      } finally {
        if (alive.current) setStarting(false);
      }
    },
    [domain, read],
  );

  const start = useCallback(() => begin(runSiteBrain), [begin]);
  const rethink = useCallback(() => begin(rethinkSiteBrain), [begin]);

  const reload = useCallback(async () => {
    await read();
  }, [read]);

  return { brain, loading, running, starting, errorKey, start, rethink, reload };
}
